import { cn } from '../../../lib/cn';

export interface PickerOption {
  id: string;
  title: string;
  /** Mono sub-line — a code, a route type, a distance. */
  subline?: string;
  detail?: string;
}

export interface PickerListProps {
  options: readonly PickerOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  emptyMessage: string;
  ariaLabel: string;
}

/**
 * The vehicle / scenario / route chooser of the wizard: a radio group drawn as cards, so it stays keyboard- and
 * screen-reader-navigable while reading as a list of things rather than a dropdown.
 */
export const PickerList = ({ options, selectedId, onSelect, emptyMessage, ariaLabel }: PickerListProps) => {
  if (options.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-8">
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
    >
      {options.map((option) => {
        const isSelected = option.id === selectedId;

        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(option.id)}
            className={cn(
              'rounded-lg border p-4 text-start transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted',
            )}
          >
            <p className="truncate text-sm font-medium text-card-foreground">{option.title}</p>
            {option.subline ? <p className="truncate font-mono text-xs text-muted-foreground">{option.subline}</p> : null}
            {option.detail ? <p className="mt-1 truncate text-xs text-muted-foreground">{option.detail}</p> : null}
          </button>
        );
      })}
    </div>
  );
};
